import {
  CampaignAnalyticsReport,
  ChartTypeName,
} from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { visitsByHourOfDayDataToSeriesData } from '@configurations/campaignAnalytics/visitsByHourOfDayChart';
import { findMaxBy } from '@utils/arrays';

const topVisibleData = <
  T extends CampaignAnalyticsReport[ChartTypeName],
  V extends object,
>(
  field: T,
  key: keyof V,
  getData: (field: T) => V[]
) => {
  return field.visible ? findMaxBy(getData(field), key) : null;
};

export const topDataFromReport = (
  campaignAnalyticsReport: CampaignAnalyticsReport
) => {
  const topGenderData = topVisibleData(
    campaignAnalyticsReport.demographicProfile,
    'value',
    field => field.data.visited.statsByGender
  );
  const topIncomeData = topVisibleData(
    campaignAnalyticsReport.demographicProfile,
    'value',
    field => field.data.visited.statsByIncome
  );
  const topStateData = topVisibleData(
    campaignAnalyticsReport.stateVisits,
    'visitIndex',
    field => field.data
  );
  const topAgeData = topVisibleData(
    campaignAnalyticsReport.demographicProfile,
    'value',
    field => field.data.visited.statsByAge
  );
  const topDmaData = topVisibleData(
    campaignAnalyticsReport.dmaVisits,
    'visitIndex',
    field => field.data
  );
  const topProximityData = topVisibleData(
    campaignAnalyticsReport.proximityToHome,
    'value',
    field => field.data
  );
  const topTimeToFirstVisitData = topVisibleData(
    campaignAnalyticsReport.timeToFirstVisitByDay,
    'percentageOfUsers',
    field => field.data
  );
  const topVisitsByTimeOfDayData = topVisibleData(
    campaignAnalyticsReport.visitsByHourOfDay,
    'y',
    field => visitsByHourOfDayDataToSeriesData(field.data)
  );
  const topVisitsByDayOfWeekData = topVisibleData(
    campaignAnalyticsReport.visitsByDayOfWeek,
    'value',
    field =>
      Object.entries(field.data).map(([name, value]) => ({ name, value }))
  );

  return {
    topGenderData,
    topIncomeData,
    topStateData,
    topAgeData,
    topDmaData,
    topProximityData,
    topTimeToFirstVisitData,
    topVisitsByTimeOfDayData,
    topVisitsByDayOfWeekData,
  };
};

export type TopData = ReturnType<typeof topDataFromReport>;
